import {i18n, type Messages, type AllMessages} from "@lingui/core";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import updateLocale from "dayjs/plugin/updateLocale";
import root from "window-or-global";

import {noTrans} from "@atg-shared/no-trans";

import {getLangCode} from "./utils/getLangCode";
import {getLanguage} from "./utils/getLanguage";

import type {LocaleString} from "@atg-shared/market-config";

dayjs.extend(LocalizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(calendar);

const {availableLocales} = root.marketConfig ?? {};

function isAllMessages(messages: Messages | AllMessages): messages is AllMessages {
    return availableLocales.every((availableLocale) => availableLocale in messages);
}

export type LoadMessages = (
    locale: LocaleString,
) => (AllMessages | Promise<AllMessages>) | (Messages | Promise<Messages>);

async function initI18n(locale: LocaleString, loadMessages: LoadMessages) {
    const messages = await loadMessages(locale);

    if (isAllMessages(messages)) {
        i18n.load(messages);
    } else {
        i18n.load(locale, messages);
    }

    i18n.activate(locale);
}

async function initDayjs(locale: LocaleString) {
    const lang = getLangCode(locale);

    switch (lang) {
        case "sv": {
            await import(/* webpackChunkName: "dayjs-locale-sv" */ "dayjs/locale/sv");
            dayjs.locale("sv");
            dayjs.updateLocale("sv", {
                calendar: {
                    lastDay: noTrans("[igår] HH:mm"),
                    sameDay: noTrans("[idag] HH:mm"),
                    nextDay: noTrans("[imorgon] HH:mm"),
                    lastWeek: noTrans("[i] dddd[s] HH:mm"),
                    nextWeek: noTrans("dddd HH:mm"),
                    sameElse: noTrans("YYYY-MM-DD"),
                },
                weekdaysShort: ["sön", "mån", "tis", "ons", "tor", "fre", "lör"],
                monthsShort: [
                    "jan",
                    "feb",
                    "mar",
                    "apr",
                    "maj",
                    "juni",
                    "juli",
                    "aug",
                    "sep",
                    "okt",
                    "nov",
                    "dec",
                ],
            });
            break;
        }
        case "fi": {
            await import(/* webpackChunkName: "dayjs-locale-fi" */ "dayjs/locale/fi");
            dayjs.locale("fi");
            dayjs.updateLocale("fi", {
                calendar: {
                    lastDay: noTrans("[eilen klo] H.mm"),
                    sameDay: noTrans("[tänään] H.mm"),
                    nextDay: noTrans("[huomenna klo] H.mm"),
                    lastWeek: noTrans("[viime] dddd[na] H.mm"),
                    nextWeek: noTrans("ddd H.mm"),
                    sameElse: noTrans("D.M.YYYY"),
                },
                weekdaysShort: ["su", "ma", "ti", "ke", "to", "pe", "la"],
                monthsShort: [
                    "tammi",
                    "helmi",
                    "maalis",
                    "huhti",
                    "touko",
                    "kesä",
                    "heinä",
                    "elo",
                    "syys",
                    "loka",
                    "marras",
                    "joulu",
                ],
            });
            break;
        }
        case "pseudo": {
            await import(
                /* webpackChunkName: "dayjs-locale-x-pseudo" */ "dayjs/locale/x-pseudo"
            );
            dayjs.locale("x-pseudo");
            dayjs.updateLocale("x-pseudo", {
                calendar: {
                    lastDay: noTrans("[ïϱåř] HH.mm"),
                    sameDay: noTrans("[ïδáϱ] HH.mm"),
                    nextDay: noTrans("[ï₥ôřϱôñ] HH.mm"),
                    lastWeek: noTrans("[ï] dddd[s] HH.mm"),
                    nextWeek: noTrans("dddd HH.mm"),
                    sameElse: noTrans("YYYY.MM.DD"),
                },
            });
            break;
        }
        // This case is for the in-context translation tool, which uses the af-ZA locale to pseudo-translate the UI
        // We load the market default dayjs locale for this case
        case "af": {
            await initDayjs(root.marketConfig.defaultLocale as LocaleString);
            return;
        }
        default: {
            throw new Error(`Unsupported locale: ${locale}`);
        }
    }
}

export async function initialiseLanguage(loadMessages: LoadMessages) {
    const {locale} = getLanguage(false);

    await Promise.all([initI18n(locale, loadMessages), initDayjs(locale)]);
}
